import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

import { BridgeHub } from './useMultiSiteConnections.types.ts';

// One live connection per site. A site's entry is replaced (and the old
// connection stopped) when that site reconnects.
const liveConnections = new Map<string, HubConnection>();

const newAbortError = (siteName: string) =>
  new DOMException(`Connection attempt to ${siteName} was canceled`, 'AbortError');

/** Stop and forget connections for sites no longer in the site list. */
export const stopConnectionsExcept = (siteIds: string[]) => {
  const keep = new Set(siteIds);
  for (const [siteId, connection] of liveConnections) {
    if (keep.has(siteId)) {
      continue;
    }
    liveConnections.delete(siteId);
    connection
      .stop()
      .then(() => {
        console.warn(`Stopped connection for site ${siteId}`);
      })
      .catch(err => {
        console.error(`Failed to stop connection for site ${siteId}`, err);
      });
  }
};

export const stopAllConnections = () => stopConnectionsExcept([]);

/**
 * Establishes a connection to a single site's bridge hub using SignalR.
 * Connection lifecycle events such as reconnecting, reconnected, and disconnected are logged.
 * Each site connects independently, so an unreachable site does not delay the others.
 *
 * @param {BridgeHub} s - The site to connect to, containing its id, name and URL.
 * @param {AbortSignal} signal - Cancels the connection attempt while it is still connecting.
 * @param {() => void} onDisconnected - Called when an established connection drops for good
 *   (after SignalR's automatic retries give up). Not called when the connection is replaced
 *   or stopped deliberately.
 * @returns {Promise<HubConnection>} The established connection. Rejects if the connection fails.
 */
export const connectToApi = async (
  s: BridgeHub,
  signal: AbortSignal,
  onDisconnected?: () => void
): Promise<HubConnection> => {
  const previous = liveConnections.get(s.id);
  if (previous) {
    liveConnections.delete(s.id);
    previous.stop().catch(err => {
      console.error(`Failed to stop previous connection for ${s.name}`, err);
    });
  }

  const connection = new HubConnectionBuilder()
    .withUrl(
      s.url
      // , { transport: HttpTransportType.WebSockets }
    )
    .withAutomaticReconnect([0, 2000, 10000, 30000]) // short transient retries
    .configureLogging(LogLevel.Information)
    .build();

  connection.onreconnecting(err => {
    console.warn(`🟡 ${s.name}: reconnecting… id=${connection.connectionId ?? 'n/a'}`, err);
  });

  connection.onreconnected(newId => {
    console.info(`🟢 ${s.name}: reconnected. id=${newId ?? 'unknown'}`);
  });

  // When the built-in retry policy gives up, we’re "Disconnected".
  connection.onclose(err => {
    // stop() also fires onclose. Only report drops of the connection that is
    // still registered — a replaced or deliberately stopped connection is
    // removed from the registry before stop(), so this stays silent for those
    // (reporting them would reconnect in a loop).
    if (liveConnections.get(s.id) !== connection) {
      return;
    }
    console.error(`🔴 ${s.name}: disconnected (no more automatic retries).`, err);
    onDisconnected?.();
  });

  // cancel behavior: if still connecting, stop; if already started, do nothing
  let started = false;
  const onAbort = () => {
    if (!started) {
      connection.stop().catch(err => {
        console.error(`Failed to stop cancelled connection for ${s.name}`, err);
      });
    }
  };
  if (signal.aborted) throw newAbortError(s.name);
  signal.addEventListener('abort', onAbort, { once: true });

  try {
    await connection.start();

    // Detect transport type using browser globals (survives minification)
    // @ts-ignore - accessing internal connection.transport
    const transport = connection.connection?.transport;
    const transportValues = Object.values(transport ?? {});
    const transportName = transportValues.some(v => v instanceof WebSocket)
      ? 'WebSockets'
      : transportValues.some(v => v instanceof EventSource)
        ? 'ServerSentEvents'
        : 'LongPolling';
    console.info(`Transport Type: ${transportName}`);
    started = true;
    if (signal.aborted) {
      // Cancelled between start() resolving and here (e.g. the site was
      // removed mid-connect): the query result is discarded, so registering
      // the connection would leak it.
      connection.stop().catch(err => {
        console.error(`Failed to stop cancelled connection for ${s.name}`, err);
      });
      throw newAbortError(s.name);
    }
    liveConnections.set(s.id, connection);
    return connection;
  } catch (e) {
    if (signal.aborted) {
      // Cancellation (unmount, site removed mid-connect) is normal control
      // flow, not a connection failure.
      throw newAbortError(s.name);
    }
    console.error(`❌ Could not connect to ${s.name} url: ${s.url}`, e);
    throw e;
  } finally {
    signal.removeEventListener('abort', onAbort);
  }
};
